import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { logsService, Log } from "../services/logs.service";
import { formatDateTime } from "../utils/format";
import "../styles/LogsPage.css";
import { getErrorMessage } from '../utils/error';

const ToAssignPage: React.FC = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState<Log[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dismissingId, setDismissingId] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const limit = 10;

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await logsService.getPendingAssignmentLogs(page, limit);
      setLogs(response.data);
      setTotalPages(response.meta.totalPages);
      setTotalLogs(response.meta.total);
      setLoading(false);
    } catch (err) {
      setError("Impossible de charger les factures a assigner");
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleLogClick = (logId: string) => {
    navigate(`/to-assign/${logId}`);
  };

  const handleDismiss = async (e: React.MouseEvent, logId: string) => {
    e.stopPropagation();
    if (!window.confirm("Voulez-vous vraiment rejeter cette facture ?")) return;

    setDismissingId(logId);
    try {
      await logsService.dismissLog(logId);
      fetchLogs();
    } catch (err) {
      alert(getErrorMessage(err, "Erreur lors du rejet"));
    } finally {
      setDismissingId(null);
    }
  };

  if (loading) {
    return <div className="loading-indicator">Chargement des factures a assigner...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="logs-container">
      <div className="logs-header-section">
        <h2>Factures a assigner</h2>
      </div>
      <p>Factures recues par e-mail dont la colocation n'a pas pu etre determinee automatiquement.</p>

      <div className="logs-list">
        <div className="logs-header">
          <div className="log-column date-column">Date</div>
          <div className="log-column file-column">Nom du fichier</div>
          <div className="log-column error-column">Expediteur</div>
          <div className="log-column actions-column">Actions</div>
        </div>

        {logs.length > 0 ? (
          logs.map(log => (
            <div
              key={log.id}
              className="log-row clickable"
              onClick={() => handleLogClick(log.id)}
            >
              <div className="log-column date-column">{formatDateTime(log.createdAt)}</div>
              <div className="log-column file-column">{log.fileName}</div>
              <div className="log-column error-column">{log.context?.senderEmail || '-'}</div>
              <div className="log-column actions-column">
                <button
                  className="dismiss-button"
                  onClick={(e) => handleDismiss(e, log.id)}
                  disabled={dismissingId === log.id}
                  title="Rejeter cette facture"
                >
                  {dismissingId === log.id ? "..." : "Rejeter"}
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="no-logs-message">
            Aucune facture en attente d'assignation.
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-button"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            Precedent
          </button>

          <span className="pagination-info">
            Page {page} sur {totalPages} ({totalLogs} factures)
          </span>

          <button
            className="pagination-button"
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
          >
            Suivant
          </button>
        </div>
      )}
    </div>
  );
};

export default ToAssignPage;
